import { useEffect } from 'react';
import { useBlocker, type Blocker } from 'react-router-dom';

import { registerUnsavedWork } from '../services/unsaved-work';

/**
 * Returned by `useDirtyStateBlocker`. Pages render their own confirmation
 * modal driven by `isBlocking`; `proceed` discards local state and lets
 * the queued navigation through; `cancel` keeps the user on the current
 * route.
 */
export interface DirtyStateBlocker {
  isBlocking: boolean;
  proceed: () => void;
  cancel: () => void;
}

/**
 * Intercepts in-app navigation while `isDirty` is true so a page can show
 * a confirm-discard modal before letting the user leave. Wraps
 * `useBlocker` with a typed surface so call sites don't have to
 * discriminate the raw blocker state machine.
 *
 * Only blocks when the pathname changes — same-pathname navigations
 * (query-string / hash updates) flow through. Each page that needs the
 * guard calls the hook locally; no shared provider is needed.
 *
 * Requires a data router (`createBrowserRouter` in App.tsx;
 * `createMemoryRouter` in tests). Under the legacy `<BrowserRouter>` /
 * `<MemoryRouter>` shape, `useBlocker` throws with "useBlocker must be
 * used within a data router" — tests that render any page using this
 * hook must use `createMemoryRouter` + `<RouterProvider>`.
 *
 * Also registers the dirty state with `unsaved-work`, so a reload the app
 * starts by itself (cross-tab scope reconciliation, DV-3421) waits for it
 * instead of discarding it — a document load never reaches the blocker.
 * Pass `survivesReload` when what the blocker guards is already saved
 * server-side and only unsynced (DV-3421 review round 6, item D): a reload
 * loses nothing there, and holding it back would only keep the tab on a
 * selection its token can no longer reach.
 */
export const useDirtyStateBlocker = (
  isDirty: boolean,
  { survivesReload = false }: { survivesReload?: boolean } = {},
): DirtyStateBlocker => {
  useEffect(
    () => (isDirty && !survivesReload ? registerUnsavedWork() : undefined),
    [isDirty, survivesReload],
  );

  const blocker: Blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  return {
    isBlocking: blocker.state === 'blocked',
    proceed: () => {
      if (blocker.state === 'blocked') blocker.proceed();
    },
    cancel: () => {
      if (blocker.state === 'blocked') blocker.reset();
    },
  };
};
